// @flow
import React from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';

import IconButton from '../IconButton/IconButton';
import {LOCATION__16} from '@homeaway/svg-defs';
import ResultsCount from '../ResultsCount/ResultsCount';
import SortDropDown from '../SortDropDown/SortDropDown';
import SortLegalDisclaimer from '../SortLegalDisclaimer/SortLegalDisclaimer';
import routerActions from '../../actions/router';
import DisclaimerMessaging from '../DisclaimerMessaging/DisclaimerMessaging';
import {media} from '../../utils/breakpoints';
import {trigger} from '../../utils/edapWrapper';

import './MetaBar.less';

import type {Shape as ResultsCountShape} from '../ResultsCount/ResultsCount';
import type {State as ReduxState} from '../../reducers';
import type {Breakpoint} from '../../utils/breakpoints';
import type {IntlShape} from 'react-intl';
import type {SystemData} from '../../../server/utils/systemData';

type StateProps = {
    breakpoint: Breakpoint,
    hasSortLegalDisclaimer: boolean,
    orderBy: ?string,
    system: SystemData
};
export type Shape = {
    onClickMapButton: typeof routerActions.map,
    resultsCount?: ResultsCountShape
};

type InjectIntlProps = {
    intl: IntlShape
};

type Props = Shape & StateProps & InjectIntlProps;

export const MetaBar = (props: Props) => {
    const {hasSortLegalDisclaimer, orderBy, intl} = props;

    const showSort = !!(!props.resultsCount || props.resultsCount.resultCount);
    const showSortLegalDisclaimer =
        hasSortLegalDisclaimer && (orderBy === 'default' || !orderBy);
    const showMapIcon =
        !media(props.breakpoint, {minWidth: 'MD_MIN'}) &&
        media(props.breakpoint, {minWidth: 'SM_MIN'});
    const handleMapButtonClick = () => {
        trigger('generic.event', {
            eventcategory: 'search',
            eventaction: 'list to map click',
            eventlabel: 'button'
        });
        props.onClickMapButton();
    };
    return (
        <div className="MetaBar" data-wdio="MetaBar">
            {showSortLegalDisclaimer && (
                <SortLegalDisclaimer
                    intl={intl}
                    breakpoint={props.breakpoint}
                    system={props.system}
                />
            )}
            {props.resultsCount && props.resultsCount.resultCount ? (
                <div
                    id="resultsCountDisclaimerHeading"
                    className="MetaBar__resultsCountWithDisclaimer"
                    role="heading"
                >
                    <ResultsCount resultsCount={props.resultsCount} />
                    <div className="sr-only">
                        <DisclaimerMessaging />
                    </div>
                </div>
            ) : (
                <div />
            )}
            <div className="MetaBar__icons">
                {showMapIcon && (
                    <IconButton
                        markup={LOCATION__16}
                        onClick={handleMapButtonClick}
                        data-wdio="map"
                    >
                        <FormattedMessage
                            id="mapButton"
                            description="Button to switch to map view"
                            defaultMessage="Map"
                        />
                    </IconButton>
                )}
                {showSort && <SortDropDown />}
            </div>
        </div>
    );
};

MetaBar.defaultProps = {
    hasSortLegalDisclaimer: false
};

const mapDispatchToProps = {
    onClickMapButton: routerActions.map
};

const mapStateToProps = (state: ReduxState): StateProps => {
    const {viewport, system, searchParams} = state;
    return {
        breakpoint: viewport.breakpoint,
        hasSortLegalDisclaimer: system.hasSortLegalDisclaimer,
        system,
        orderBy: searchParams.params.orderBy
    };
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(MetaBar);
